import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import s from './MoreOption.module.scss';

const MoreOption = ({ 
  dataArray = [], 
  componentDidMount, 
  componentDidUpdate, 
  componentWillUnmount 
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef(null);

  // Handle document click to close dropdown
  useEffect(() => {
    const handleDocumentClick = (event) => {
      if (buttonRef.current && !buttonRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('click', handleDocumentClick);
    }

    return () => {
      document.removeEventListener('click', handleDocumentClick);
    };
  }, [showDropdown]);

  // Lifecycle effect
  useEffect(() => {
    componentDidMount?.();
    
    return () => {
      componentWillUnmount?.();
      setShowDropdown(false);
    };
  }, [componentDidMount, componentWillUnmount]);

  // Component update effect
  useEffect(() => {
    componentDidUpdate?.();
  }, [componentDidUpdate, dataArray]);

  const handleToggle = (event) => {
    event.stopPropagation();
    
    if (!showDropdown && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 4,
        left: Math.min(
          rect.right - 160 + window.scrollX, 
          window.innerWidth - 170
        )
      });
    }
    setShowDropdown(!showDropdown);
  };

  const handleItemClick = (clickHandler) => {
    if (clickHandler) {
      clickHandler();
    }
    setShowDropdown(false);
  };

  const handleLinkClick = () => {
    setShowDropdown(false);
  };

  if (!dataArray.length) {
    return null;
  }

  return (
    <>
      <div className={s.wsaDropdown}>
        <button
          ref={buttonRef}
          className={s.moreButton}
          onClick={handleToggle}
          type="button"
          aria-label="More options"
          title="More options"
        >
          <span className={s.dots}>⋯</span>
        </button>
      </div>
      
      {showDropdown && createPortal(
        <div 
          className={s.dropdownMenu}
          style={{
            position: 'absolute',
            top: dropdownPosition.top,
            left: dropdownPosition.left,
            zIndex: 1050,
          }}
        >
          {dataArray.map((item, index) => {
            if (!item.Name) return null;
            
            if (item.Url) {
              return (
                <Link 
                  key={index} 
                  to={item.Url} 
                  className={s.dropdownItem}
                  onClick={handleLinkClick}
                >
                  {item.Name}
                </Link>
              );
            }
            
            if (item.Click) {
              return (
                <button 
                  key={index} 
                  onClick={() => handleItemClick(item.Click)}
                  className={s.dropdownItem}
                  type="button"
                >
                  {item.Name}
                </button>
              );
            }
            
            return null;
          })}
        </div>,
        document.body
      )}
    </>
  );
};

export default MoreOption;